import type { SubscriptionTier, BillingInterval, Subscription } from "@prisma/client";
import { TIER_LIMITS, getTotalStorageBytes, getBandwidthLimit, getTotalBandwidthBytes, getEffectiveHostedVideoLimit, type TierLimits } from "./constants";
import { isSubscriptionBypassed } from "./bypass";

/**
 * Check if tier allows recording videos via the desktop app
 */
export function canRecord(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canRecord;
}

/**
 * Check if tier allows embedding videos on external sites
 */
export function canEmbed(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canEmbed;
}

/**
 * Check if tier allows uploading pre-existing video files
 */
export function canUpload(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canUpload;
}

/**
 * Check if tier has access to video analytics
 */
export function canAccessAnalytics(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canAnalytics;
}

/**
 * Check if tier allows custom branding on sharing page
 */
export function canCustomBranding(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canCustomBranding;
}

/**
 * Check if tier allows password protecting videos
 */
export function canPasswordProtect(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canPasswordProtect;
}

/**
 * Check if tier allows domain whitelisting for embeds
 */
export function canDomainWhitelist(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canDomainWhitelist;
}

/**
 * Get maximum video resolution for tier
 * @returns "1080p" or "4k"
 */
export function getMaxResolution(tier: SubscriptionTier): "1080p" | "4k" {
  return TIER_LIMITS[tier].maxResolution;
}

/**
 * Get maximum recording duration in minutes for tier
 * @returns Duration in minutes, or null for unlimited
 */
export function getMaxDurationMinutes(tier: SubscriptionTier): number | null {
  return TIER_LIMITS[tier].maxDurationMinutes;
}

/**
 * Get total storage limit in bytes for subscription
 * Includes base tier storage plus any extra storage add-ons
 */
export function getStorageLimitBytes(
  tier: SubscriptionTier,
  extraStorageUnits: number
): bigint {
  return getTotalStorageBytes(tier, extraStorageUnits);
}

/**
 * Get bandwidth limit in bytes for subscription
 * Returns null for RECORDING tier (no embedding/bandwidth)
 * Includes base tier bandwidth plus any extra bandwidth add-ons
 */
export function getBandwidthLimitBytes(
  tier: SubscriptionTier,
  billingInterval: BillingInterval,
  extraBandwidthUnits: number = 0
): bigint | null {
  return getTotalBandwidthBytes(tier, billingInterval, extraBandwidthUnits);
}

/**
 * Check if tier has access to advanced analytics (heatmaps, single-user breakdown)
 */
export function canAdvancedAnalytics(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canAdvancedAnalytics;
}

/**
 * Check if tier has access to engagement optimization tooling
 */
export function canEngagementOptimization(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canEngagementOptimization;
}

/**
 * Check if tier has access to A/B testing
 */
export function canABTesting(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canABTesting;
}

/**
 * Check if tier allows customizing player/page colors
 */
export function canChangeColors(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canChangeColors;
}

/**
 * Check if tier allows chapter markers
 */
export function canChapters(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canChapters;
}

/**
 * Check if tier allows subtitles/captions
 */
export function canSubtitles(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canSubtitles;
}

/**
 * Check if tier allows exporting an edited video from the editor
 */
export function canExportFromEditor(tier: SubscriptionTier): boolean {
  if (isSubscriptionBypassed()) return true;
  return TIER_LIMITS[tier].canExportFromEditor;
}

/**
 * Get the hosted-video count limit for tier.
 * @returns max number of hosted videos, or null for unlimited (legacy CREATOR).
 */
export function getHostedVideoLimit(tier: SubscriptionTier): number | null {
  if (isSubscriptionBypassed()) return null;     // unlimited in bypass mode
  return TIER_LIMITS[tier].hostedVideoLimit;
}

/**
 * Get the team-member count limit for tier.
 */
export function getTeamMemberLimit(tier: SubscriptionTier): number {
  if (isSubscriptionBypassed()) return 100;      // effectively unlimited in bypass mode
  return TIER_LIMITS[tier].teamMemberLimit;
}

/**
 * Combine the capabilities of all of a user's subscriptions into a single TierLimits-like
 * view. Booleans are OR'd; numeric limits are MAX'd. Useful for UI components that need
 * to know "what can this user do at all?" without caring which subscription unlocks it.
 *
 * If the user has no subscriptions, returns a fully-restricted "no access" view.
 */
export function aggregateFeatures(subscriptions: Subscription[]): TierLimits {
  if (subscriptions.length === 0) {
    return {
      storageBytes: 0n,
      bandwidthBytesMonthly: 0n,
      bandwidthBytesYearly: 0n,
      bandwidthOveragePerGB: null,
      maxResolution: "1080p",
      maxDurationMinutes: 0,
      hostedVideoLimit: 0,
      teamMemberLimit: 0,
      canRecord: false, canEmbed: false, canUpload: false,
      canAnalytics: false, canAdvancedAnalytics: false,
      canEngagementOptimization: false, canABTesting: false,
      canCustomBranding: false, canChangeColors: false,
      canChapters: false, canSubtitles: false, canExportFromEditor: false,
      canPasswordProtect: false, canDomainWhitelist: false,
    };
  }
  const limits = subscriptions.map((s) => TIER_LIMITS[s.tier]);
  // Per-sub effective hosted-video limit honors any customHostedVideoLimit
  // override before we MAX across the user's subs.
  const hostedVideoLimits = subscriptions.map((s) =>
    getEffectiveHostedVideoLimit(s.tier, s.customHostedVideoLimit)
  );
  // Helper for MAX of bigint | null (null treated as unlimited → null wins).
  const maxBigint = (vals: (bigint | null)[]): bigint | null => {
    if (vals.some((v) => v === null)) return null;
    return vals.reduce((max, v) => (v! > max ? v! : max), 0n);
  };
  // Helper for MAX of number | null (null treated as unlimited).
  const maxNumberOrNull = (vals: (number | null)[]): number | null => {
    if (vals.some((v) => v === null)) return null;
    return Math.max(...(vals as number[]));
  };
  return {
    storageBytes: maxBigint(limits.map((l) => l.storageBytes)) ?? 0n,
    bandwidthBytesMonthly: maxBigint(limits.map((l) => l.bandwidthBytesMonthly)),
    bandwidthBytesYearly: maxBigint(limits.map((l) => l.bandwidthBytesYearly)),
    bandwidthOveragePerGB: limits.reduce<number | null>((min, l) =>
      l.bandwidthOveragePerGB === null ? min :
      min === null ? l.bandwidthOveragePerGB :
      Math.min(min, l.bandwidthOveragePerGB), null),
    maxResolution: limits.some((l) => l.maxResolution === "4k") ? "4k" : "1080p",
    maxDurationMinutes: maxNumberOrNull(limits.map((l) => l.maxDurationMinutes)),
    hostedVideoLimit: maxNumberOrNull(hostedVideoLimits),
    teamMemberLimit: Math.max(...limits.map((l) => l.teamMemberLimit)),
    canRecord:                  limits.some((l) => l.canRecord),
    canEmbed:                   limits.some((l) => l.canEmbed),
    canUpload:                  limits.some((l) => l.canUpload),
    canAnalytics:               limits.some((l) => l.canAnalytics),
    canAdvancedAnalytics:       limits.some((l) => l.canAdvancedAnalytics),
    canEngagementOptimization:  limits.some((l) => l.canEngagementOptimization),
    canABTesting:               limits.some((l) => l.canABTesting),
    canCustomBranding:          limits.some((l) => l.canCustomBranding),
    canChangeColors:            limits.some((l) => l.canChangeColors),
    canChapters:                limits.some((l) => l.canChapters),
    canSubtitles:               limits.some((l) => l.canSubtitles),
    canExportFromEditor:        limits.some((l) => l.canExportFromEditor),
    canPasswordProtect:         limits.some((l) => l.canPasswordProtect),
    canDomainWhitelist:         limits.some((l) => l.canDomainWhitelist),
  };
}
